import * as React from 'react';
import { X as XIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type ToastVariant = 'default' | 'success' | 'error' | 'warning';

export interface ToastInput {
  message: string;
  variant?: ToastVariant;
  /** Auto-dismiss in ms. Default 4000. Pass 0 for sticky. */
  duration?: number;
  action?: { label: string; onClick: () => void };
}

interface ToastRecord extends ToastInput {
  id: string;
}

interface ToastContextValue {
  toast: (input: ToastInput) => string;
  dismiss: (id?: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

// Provider keeps the list of active toasts in state. The viewport reads
// from the same context — we don't bother with refs because re-rendering
// 1-3 toast cards is cheap.
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastRecord[]>([]);

  const dismiss = React.useCallback((id?: string) => {
    setToasts((prev) => (id ? prev.filter((t) => t.id !== id) : []));
  }, []);

  const toast = React.useCallback((input: ToastInput) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const record: ToastRecord = { ...input, id };
    setToasts((prev) => [...prev, record]);

    const duration = input.duration ?? 4000;
    if (duration > 0) {
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
    return id;
  }, []);

  const value = React.useMemo<ToastContextValue>(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

/**
 * Imperative API matching common React patterns. Returns no-ops when called
 * outside a provider so silent migration paths (e.g. a hook used in a page
 * that the test harness renders without the provider) don't crash.
 */
export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) {
    return { toast: () => '', dismiss: () => undefined };
  }
  return ctx;
}

interface ToastViewportProps {
  toasts: ToastRecord[];
  onDismiss: (id: string) => void;
}

// Variant border tones are chosen to match the rest of the migrated UI
// (emerald = success, amber = warning, rose = error). Background stays
// neutral so the toast still reads as "system-level UI" rather than
// inline alert content.
const VARIANT_CLASSES: Record<ToastVariant, string> = {
  default: 'border-border',
  success: 'border-emerald-500/40',
  error: 'border-rose-500/40',
  warning: 'border-amber-500/40',
};

// z-50 keeps toasts above modal overlays (which sit at z-50 themselves);
// the layered card + shadow visually separates them.
function ToastViewport({ toasts, onDismiss }: ToastViewportProps) {
  if (toasts.length === 0) return null;
  return (
    <div
      role="region"
      aria-label="Notifications"
      className="fixed inset-x-0 bottom-4 z-50 mx-auto flex w-fit max-w-md flex-col items-center space-y-2"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          role="status"
          aria-live="polite"
          className={cn(
            'flex items-center gap-3 rounded-xl border bg-background/95 px-4 py-2.5 shadow-lg backdrop-blur',
            VARIANT_CLASSES[t.variant ?? 'default'],
          )}
        >
          <span className="text-sm">{t.message}</span>
          {t.action && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                t.action!.onClick();
                onDismiss(t.id);
              }}
            >
              {t.action.label}
            </Button>
          )}
          <button
            type="button"
            onClick={() => onDismiss(t.id)}
            aria-label="Dismiss"
            className="text-muted-foreground transition-colors hover:text-foreground"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
